import { useState } from 'react';
import { signInWithProvider, type OAuthProvider } from '../../lib/oauth';
import { OAuthButton } from '../design-system';

interface OAuthButtonsProps {
  /** Cream buttons + cream divider, for a fixed dark/violet backdrop that
   *  doesn't track the app's light/dark theme (e.g. auth screens). */
  inverted?: boolean;
}

/**
 * Google + Apple sign-in buttons. Works on web (full-page redirect) and on
 * native (in-app browser + deep-link callback) via lib/oauth + lib/native.
 */
export function OAuthButtons({ inverted = false }: OAuthButtonsProps) {
  const [pending, setPending] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState('');

  async function handle(provider: OAuthProvider) {
    setError('');
    setPending(provider);
    try {
      await signInWithProvider(provider);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
      setPending(null);
    }
    // On success the page redirects (web) or a deep link completes auth (native),
    // so we intentionally leave the spinner running.
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className={`flex items-center gap-3 ${inverted ? '' : 'text-app-muted'}`}
        style={inverted ? { color: 'rgba(244, 236, 210, .55)' } : undefined}
      >
        <span className={`h-px flex-1 ${inverted ? '' : 'bg-app-border'}`} style={inverted ? { background: 'rgba(244, 236, 210, .2)' } : undefined} />
        <span className="text-xs uppercase tracking-wide">or</span>
        <span className={`h-px flex-1 ${inverted ? '' : 'bg-app-border'}`} style={inverted ? { background: 'rgba(244, 236, 210, .2)' } : undefined} />
      </div>

      <OAuthButton
        provider="google"
        onClick={() => handle('google')}
        disabled={pending !== null}
        loading={pending === 'google'}
        style={inverted ? { background: 'rgb(var(--cream))', color: 'rgb(var(--ink))', border: 'none' } : undefined}
      />

      {/* Apple sign-in — disabled until Apple Developer enrollment completes */}
      <OAuthButton
        provider="apple"
        label="Apple — coming soon"
        disabled
        style={inverted ? { background: 'rgba(244, 236, 210, .5)', color: 'rgba(28, 27, 25, .5)', border: 'none' } : undefined}
      />

      {error && <p className="text-sm text-crimson text-center">{error}</p>}
    </div>
  );
}
